'use client';

import { motion, AnimatePresence } from 'motion/react';
import Image from 'next/image';
import { useState, useEffect, useMemo, memo, useRef } from 'react';

import { cn } from '@/core/utils/utils';

import { safeParseMarkdown, type ParsedBlock } from '../lib/parser';

import { ParsedContent } from './parser-components';

interface AssistantMessageProps {
    messageId: string;
    text: string;
    isStreaming: boolean;
    isExpanded: boolean;
    onCollectionSubmit?: (answer: string | string[] | number, question: string, type: string) => void;
    onCTAContactSubmit?: (contactType: string, contactValue: string) => void;
}

/**
 * Phased status labels that rotate to show "work happening"
 * Creates impression of sophisticated multi-step AI processing
 */
const THINKING_PHASES = [
    "Let me think about this...",
    "Reviewing relevant context...",
    "Connecting the dots...",
    "Considering best approach...",
    "Gathering my thoughts...",
    "Almost there...",
    "Putting it together...",
    "Just a moment more...",
];

// Stagger delay between each block reveal (in ms)
const BLOCK_STAGGER_DELAY = 80;
const BLOCK_FADE_DURATION = 0.25;

/**
 * Premium "Reveal Streaming" Pattern with:
 * 1. Rotating phased status labels during streaming
 * 2. Staggered block-by-block reveal on completion
 * 3. Smooth animations throughout
 */
export const AssistantMessage = memo(function AssistantMessage({
    messageId,
    text,
    isStreaming,
    isExpanded,
    onCollectionSubmit,
    onCTAContactSubmit
}: AssistantMessageProps) {
    const [isRevealed, setIsRevealed] = useState(!isStreaming);
    const [showContent, setShowContent] = useState(!isStreaming);
    const [currentPhase, setCurrentPhase] = useState(0);

    // Parse markdown ONLY when message is complete (not streaming)
    const parsedBlocks = useMemo(() => {
        if (isStreaming) return null;
        return safeParseMarkdown(text);
    }, [text, isStreaming]);

    // Rotate through thinking phases during streaming
    useEffect(() => {
        if (!isStreaming) return;

        const interval = setInterval(() => {
            setCurrentPhase(prev => (prev + 1) % THINKING_PHASES.length);
        }, 1500); // 1.5s rotation for better coverage

        return () => clearInterval(interval);
    }, [isStreaming]);

    // Trigger reveal animation when streaming completes
    useEffect(() => {
        if (!isStreaming && !isRevealed) {
            const timer = setTimeout(() => {
                setIsRevealed(true);
                setShowContent(true);
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [isStreaming, isRevealed]);

    // Track previous streaming state to reset only when transitioning TO streaming
    const prevStreamingRef = useRef(isStreaming);

    useEffect(() => {
        // Only reset when we transition INTO streaming (not while streaming)
        if (isStreaming && !prevStreamingRef.current) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: resetting local UI state when prop changes
            setIsRevealed(false);
            setShowContent(false);
            setCurrentPhase(0);
        }
        prevStreamingRef.current = isStreaming;
    }, [isStreaming, messageId]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(
                "flex gap-3 text-sm justify-start",
                isExpanded && "gap-4 text-[15px]"
            )}
        >
            {/* Avatar */}
            <div className={cn(
                "w-7 h-7 rounded-lg overflow-hidden shrink-0 mt-0.5 border border-primary/15 bg-gradient-to-br from-primary/10 to-primary/5",
                isExpanded && "w-8 h-8"
            )}>
                <Image
                    src="/author/hamid.png"
                    alt="Hamid"
                    width={32}
                    height={32}
                    className={cn(
                        "w-full h-full object-cover",
                        isStreaming && "animate-pulse"
                    )}
                />
            </div>

            {/* Message Content */}
            <div
                className={cn(
                    "max-w-[85%] bg-muted/60 rounded-2xl rounded-tl-md border border-border/30 px-4 py-3 min-w-[140px]",
                    isExpanded && "max-w-2xl"
                )}
            >
                <AnimatePresence mode="wait">
                    {isStreaming ? (
                        /* Streaming: Show rotating phased status */
                        <motion.div
                            key="thinking"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0, scale: 0.98 }}
                            transition={{ duration: 0.15 }}
                            className="flex items-center gap-2.5 py-0.5"
                        >
                            {/* Gradient shimmer indicator */}
                            <div className="relative flex items-center gap-1">
                                <motion.div
                                    className="w-1.5 h-1.5 rounded-full bg-primary/70"
                                    animate={{
                                        scale: [1, 1.3, 1],
                                        opacity: [0.7, 1, 0.7]
                                    }}
                                    transition={{
                                        duration: 1.2,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                />
                                <motion.div
                                    className="w-1.5 h-1.5 rounded-full bg-primary/70"
                                    animate={{
                                        scale: [1, 1.3, 1],
                                        opacity: [0.7, 1, 0.7]
                                    }}
                                    transition={{
                                        duration: 1.2,
                                        repeat: Infinity,
                                        delay: 0.2,
                                        ease: "easeInOut"
                                    }}
                                />
                                <motion.div
                                    className="w-1.5 h-1.5 rounded-full bg-primary/70"
                                    animate={{
                                        scale: [1, 1.3, 1],
                                        opacity: [0.7, 1, 0.7]
                                    }}
                                    transition={{
                                        duration: 1.2,
                                        repeat: Infinity,
                                        delay: 0.4,
                                        ease: "easeInOut"
                                    }}
                                />
                            </div>

                            {/* Rotating phase label */}
                            <AnimatePresence mode="wait">
                                <motion.span
                                    key={currentPhase}
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -4 }}
                                    transition={{ duration: 0.2 }}
                                    className="text-xs text-muted-foreground font-medium"
                                >
                                    {THINKING_PHASES[currentPhase]}
                                </motion.span>
                            </AnimatePresence>
                        </motion.div>
                    ) : showContent && parsedBlocks ? (
                        /* Completed: Staggered block-by-block reveal */
                        <motion.div
                            key="content"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.15 }}
                        >
                            <StaggeredBlockReveal
                                blocks={parsedBlocks}
                                onCollectionSubmit={onCollectionSubmit}
                                onCTAContactSubmit={onCTAContactSubmit}
                            />
                        </motion.div>
                    ) : (
                        /* Fallback: Plain text */
                        <span>{text}</span>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
});

/**
 * Staggered Block Reveal Component
 * Renders each block with sequential fade-in animation
 */
interface StaggeredBlockRevealProps {
    blocks: ParsedBlock[];
    onCollectionSubmit?: (answer: string | string[] | number, question: string, type: string) => void;
    onCTAContactSubmit?: (contactType: string, contactValue: string) => void;
}

function StaggeredBlockReveal({ blocks, onCollectionSubmit, onCTAContactSubmit }: StaggeredBlockRevealProps) {
    return (
        <div className="space-y-2">
            {blocks.map((block, index) => (
                <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 6, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                        duration: BLOCK_FADE_DURATION,
                        delay: index * (BLOCK_STAGGER_DELAY / 1000),
                        ease: [0.4, 0, 0.2, 1]
                    }}
                >
                    <ParsedContent
                        blocks={[block]}
                        onCollectionSubmit={(answer) => {
                            if (block.type === 'collection' && block.collection && onCollectionSubmit) {
                                onCollectionSubmit(
                                    answer,
                                    block.collection.label,
                                    block.collection.collectionType
                                );
                            }
                        }}
                        onCollectionSkip={() => { }}
                        onCTAContactSubmit={onCTAContactSubmit}
                    />
                </motion.div>
            ))}
        </div>
    );
}
